import React, {ReactNode, useEffect, useState} from "react";
import {getItem, getItems, getRaw} from "../../utils/communication";
import NodeCard from "../../components/note/node-card";
import Pagination from "../../components/pagination";
import {NoteNode} from "../../model/node";
import {Link, useParams} from "react-router-dom";
import ErrorDisplay from "../../components/error-display";
import HfMarked, {TOCItem} from "../../components/hf-marked";
import BreadCrumbs from "../../components/note/breadcrumbs";
import parseAnError from "../../model/misc";

export default function NodeHome() {
    const [node, setNode] = useState<NoteNode | null>(null),
        [marked, setMarked] = useState<ReactNode | null>(null),
        [tocItems, setTOCItems] = useState<TOCItem[]>([]),
        [error, setError] = useState<string | null>(null);

    let path = useParams()["*"] ?? "";

    useEffect(() => {
        document.title = "Nodes - Hoarfroster";
    }, []);

    useEffect(() => {
        if (path !== "") {
            getNode().then(() => {
            });
        } else {
            setNode({
                deletedAt: null,
                description: "",
                label: "",
                parent: null,
                tagId: "",
                publicId: "",
                path: "",
                name: "",
                content: "",
                nodeType: "Directory",
                permission: 0,
                createdAt: new Date(),
                updatedAt: new Date(),
                children: []
            })
        }
    }, [path]);

    const getNode = async () => {
        let response = await getItem<NoteNode>(`/note/note/${path}:/`);

        if (response.code !== 200) {
            setError(parseAnError(response.data).message);
        } else {
            setNode(response.data as NoteNode);
        }
    }

    useEffect(() => {
        setMarked(
            <HfMarked
                onTOCGenerated={(items: [TOCItem]) => setTimeout(() => setTOCItems(items), 1000)}>
                {node?.content}
            </HfMarked>
        );
    }, [node]);

    return (
        <main>
            <div className="container">
                <div className="content space-y-4">
                    <BreadCrumbs path={path}/>

                    <NodeDetail node={node}>
                        {marked}
                    </NodeDetail>

                    <ErrorDisplay error={error}/>
                </div>

                <div className="sidebar space-y-2">
                    <h2>Table of Contents</h2>
                    <div className="toc">
                        {tocItems.map(tocItem => (
                            <div key={tocItem.id} aria-level={tocItem.level}>
                                <Link to={`#${tocItem.id}`} onClick={() => {
                                    document.getElementById(tocItem.id)?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start'
                                    });
                                }}>{tocItem.name}</Link>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </main>
    );
}

function NodeDetail({node, children}: { node: NoteNode | null, children: ReactNode }) {
    if (node === null) {
        return (<p>Loading...</p>);
    } else if (node.createdAt == null) {
        return (<p>Not found.</p>);
    } else {
        return (
            <>
                {children}
                {node.nodeType === "Directory" ? <NodeList node={node}/> : null}
                <p className="text-8 text-sm">Edit this note? <Link to={"/note/edit/" + node.path}
                                                                    className="text-primary-16">Transport.</Link></p>
                <p className="text-8 text-sm">Add a note? <Link to={"/note/edit/" + node.path + "?mode=new"}
                                                                className="text-primary-16">Transport.</Link></p>
                <p className="text-8 text-sm">PublicID {node.publicId}</p>
                <p className="text-8 text-sm">Created {node.createdAt.toString()}</p>
                <p className="text-8 text-sm">Last Updated {node.updatedAt.toString()}</p>
            </>
        );
    }
}

function NodeList({node}: { node: NoteNode }) {
    const [loading, setLoading] = useState(false),
        [count, setCount] = useState<number>(1),
        [nodes, setNodes] = useState<NoteNode[]>([]),
        [offset, setOffset] = useState<number>(1),
        [error, setError] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            let response = await getRaw(`/note/note/${node.path}:/count`);

            if (response.code !== 200) {
                setError(parseAnError(response.data).message);
            } else {
                setCount(Math.ceil((response.data.count as number) / 10));
            }
        })();

        getNodes((offset - 1) * 10).then(() => {
        });
    }, [node.path]);

    const getNodes = async (offset: number) => {
        setLoading(true);
        let response = await getItems<NoteNode>(`/note/note/${node.path}:/children?offset=${offset}&limit=10`);

        if (response.code !== 200) {
            setError(parseAnError(response.data).message);
        } else {
            setNodes(response.data as NoteNode[]);
        }
        setLoading(false);
    }

    return (
        <div className="border-t border-gray-300 dark:border-gray-800 py-4">
            <h2>Children</h2>

            <NodeListContent nodes={nodes} offset={offset} setOffset={setOffset} count={count} loading={loading}/>

            <ErrorDisplay error={error}/>
        </div>
    );
}

function NodeListContent({nodes, offset, setOffset, count, loading}: {
    nodes: NoteNode[],
    offset: number,
    setOffset: (offset: number) => void,
    count: number,
    loading: boolean
}) {
    if (loading) {
        return (<p>Loading...</p>);
    } else if (nodes.length === 0) {
        return (<p>No children.</p>);
    } else {
        return (<>
            {nodes.map((node) => (
                <NodeCard node={node} key={node.path}/>
            ))}

            <Pagination currentPage={offset} totalPages={count} onPageChange={setOffset}/>
        </>);
    }
}